import React, { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { PageHeader, SectionTitle } from './PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { logout as hardLogout } from '@/utils/auth';

interface SettingsPageProps {
  userName: string;
}

const languageOptions = [
  { code: 'en-US', label: 'English (US)', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'en-GB', label: 'English (UK)', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'sv-SE', label: 'Svenska', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'de-DE', label: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'fr-FR', label: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'es-ES', label: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'ja-JP', label: '\u65E5\u672C\u8A9E', flag: '\u{1F1EF}\u{1F1F5}' },
];

export const SettingsPage: React.FC<SettingsPageProps> = ({ userName }) => {
  const { user, logout } = useAuth();
  const effectiveLogout = logout ?? hardLogout;

  const [language, setLanguage] = useState('en-US');
  const [languageOpen, setLanguageOpen] = useState(false);

  const derivedUserName = useMemo(() => {
    const first = typeof user?.first_name === 'string' ? user.first_name.trim() : '';
    const last = typeof user?.last_name === 'string' ? user.last_name.trim() : '';
    const full = `${first} ${last}`.trim();
    if (full) return full;
    const name = typeof user?.name === 'string' ? user.name.trim() : '';
    return name || userName;
  }, [user?.first_name, user?.last_name, user?.name, userName]);

  const primaryEmail = typeof user?.email === 'string' ? user.email : '';

  const selectedLang = languageOptions.find((l) => l.code === language) || languageOptions[0];

  return (
    <div className="max-w-[1600px] mx-auto p-6 lg:p-8 bg-[#f8f9fa] min-h-screen">
      {/* ── Branded Page Header ── */}
      <PageHeader
        category="ACCOUNT"
        title="Settings"
        subtitle="Manage your profile and preferences."
      />

      {/* Settings Sections */}
      <div className="space-y-0">

        {/* ── Profile Info ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-gray-100">
          <div className="lg:col-span-4">
            <SectionTitle
              title="Profile"
              subtitle="Your account information."
            />
          </div>
          <div className="lg:col-span-8 space-y-5">
            <div className="w-full max-w-lg">
              <label className="text-[#344054] text-xs mb-1.5 block">Name</label>
              <div className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50 text-[#101828] text-sm flex items-center">
                {derivedUserName}
              </div>
            </div>

            {primaryEmail && (
              <div className="w-full max-w-lg">
                <label className="text-[#344054] text-xs mb-1.5 block">Email</label>
                <div className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50 text-[#101828] text-sm flex items-center">
                  {primaryEmail}
                </div>
              </div>
            )}

            {/* Language */}
            <div className="relative w-full max-w-lg">
              <label className="text-[#344054] text-xs mb-1.5 flex items-center gap-1.5">
                Language
                <span className="w-3.5 h-3.5 rounded-full border border-gray-300 flex items-center justify-center text-[8px] text-gray-400 cursor-help" title="Language for the Omni Ad interface">?</span>
              </label>
              <button
                type="button"
                onClick={() => setLanguageOpen(!languageOpen)}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm flex items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">{selectedLang.flag}</span>
                  {selectedLang.label}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${languageOpen ? 'rotate-180' : ''}`} />
              </button>
              {languageOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLanguageOpen(false)} />
                  <div className="absolute z-20 w-full mt-1.5 bg-white rounded-xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden py-1">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          setLanguage(lang.code);
                          setLanguageOpen(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                          lang.code === language ? 'text-[#be43ff] bg-[#be43ff]/5' : 'text-[#344054]'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        {lang.label}
                        {lang.code === language && <Check className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Sign Out ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-gray-100">
          <div className="lg:col-span-4">
            <SectionTitle
              title="Sign out"
              subtitle="Log out of your Omni Ad account."
            />
          </div>
          <div className="lg:col-span-8 max-w-lg">
            <button
              type="button"
              onClick={() => effectiveLogout()}
              className="inline-flex items-center justify-center gap-2 px-8 py-2.5 rounded-xl text-sm bg-[#2e024c] text-white hover:bg-[#3a0d5e] transition-all active:scale-[0.97] shadow-sm"
              style={{ fontWeight: 600 }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 pt-8 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#98a2b3]">
          <span>&copy; 2026 Omni Ad. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Product updates</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Privacy Policy</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Terms of Service</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Cookie-settings</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
